import { IVehicle } from "@interfaces/IVehicle";
import { VehicleService } from "@services/vehicle.service";
import * as angular from "angular";
import * as moment from "moment";
import { AlcolockEventCode, AlcolockEventGroup } from "./alcolock.enums";
import { IAlcolockRecord, IAlcolockResult } from "./alcolock.interfaces";
import { TranslationService } from "@services/translation.service";

export class AlcolockService {

    static $inject = ["$http", "vehicleService", "translationService"];

    constructor(
        private readonly $http: angular.IHttpService,
        private readonly vehicleService: VehicleService,
        private readonly translationService: TranslationService
    ) {}

    getLatestAlcolockRecords(): angular.IPromise<IAlcolockRecord[]> {
        return this
            .$http({
                url: `/api/alcolock/latest`,
                method: "GET",
            })
            .then((response: angular.IHttpResponse<IAlcolockRecord[]>) => {
                return response.data;
            });
    }

    getAlcolockResults(startTime: string | Date, endTime: string | Date): angular.IPromise<IAlcolockResult[]> {
        return this.vehicleService.getVehicles({ includePeripheralMappings: true }).then(vehicles => {
            return this
                .$http({
                    url: `/api/alcolock/getalcolockrecords`,
                    method: "GET",
                    params: {
                        start: moment(startTime).toISOString(),
                        end: moment(endTime).toISOString()
                    }
                })
                .then((response: angular.IHttpResponse<IAlcolockRecord[]>) => {
                    return this.processAlcolockRecords(response.data, vehicles);
                });
        });
    }

    getAlcolockTop5PerVehicle(startTime: string | Date, endTime: string | Date): angular.IPromise<IAlcolockResult[]> {
        return this.vehicleService.getVehicles({ includePeripheralMappings: true }).then(vehicles => {
            return this
                .$http({
                    url: "/api/alcolock/topfiverecordsgroupedbyvehicle",
                    method: "GET",
                    params: {
                        start: moment(startTime).toISOString(),
                        end: moment(endTime).toISOString()
                    }
                })
                .then((response: angular.IHttpResponse<IAlcolockRecord[]>) => {
                    var results = this.processAlcolockRecords(response.data, vehicles);

                    var vehicleIds = results.map(m => m.VehicleId);

                    var uniqueVehicleIds = vehicleIds.filter(function (item, pos) {
                        return vehicleIds.indexOf(item) === pos;
                    });

                    uniqueVehicleIds.forEach(v => {
                        var record = results.filter(p => p.VehicleId === v).sort((one, two) => (one.DateTime > two.DateTime ? -1 : 1)).firstOrDefault<IAlcolockResult>();
                        record.Show = true;
                    });

                    return results;
                });
        });
    }

    getVehicleAlcolockResults(id: number, startTime: string | Date, endTime: string | Date): angular.IPromise<IAlcolockResult[]> {
        return this
            .$http({
                url: `/api/alcolock/vehicle/${id}/history`,
                method: "GET",
                params: {
                    start: moment(startTime).toISOString(),
                    end: moment(endTime).toISOString()
                }
            })
            .then((response: angular.IHttpResponse<IAlcolockRecord[]>) => {

                var alcolockResults: IAlcolockResult[] = [];

                response.data.forEach(record => {
                    alcolockResults.push(this.map(record));
                });

                return alcolockResults.sort((one, two) => (one.DateTime > two.DateTime ? 1 : -1));
            });
    }

    processAlcolockRecords(alcolockRecords: IAlcolockRecord[], vehicles: IVehicle[]): IAlcolockResult[] {
        var alcolockResults: IAlcolockResult[] = [];

        alcolockRecords.sort().forEach(record => {

            var vehicle = vehicles.firstOrDefault<IVehicle>(v => v.Id === record.VehicleId);

            if (vehicle) {

                alcolockResults.push(this.map(record, vehicle));
            }
        });

        return alcolockResults;
    }

    map(record: IAlcolockRecord, vehicle: IVehicle = null): IAlcolockResult {

        var alcolockResult: IAlcolockResult = {
            Reading: record.AlcoholMeasurement,
            DateTime: record.DateTime,
            VehicleId: record.VehicleId,
            ResultType: record.EventGroup,
            Id: record.PlotId,
            Vehicle: vehicle,
            Details: this.mapEventCode(record.EventCode),
            FormattedReading: this.createFormattedReading(record),
            Show: false,
            DriverId: record.DriverId,
            PlotId: record.PlotId,
            PortableDeviceSerial: record.PortableDeviceSerial
        };

        return alcolockResult;
    }

    createFormattedReading(record: IAlcolockRecord): string {
        if (record.EventGroup === AlcolockEventGroup.SignificantEvent) {
            return "N/A";
        }

        var mgl = record.AlcoholMeasurement / 1000;

        return `${mgl}mg/L`
    }

    downloadCsv(list: any[], filename: string = 'export.csv') {

        let csv = this.convertArrayOfObjectsToCsv({
            data: list
        });

        if (csv == null) {
            return;
        }

        var blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

        if (navigator.msSaveBlob) { // IE 10+
            navigator.msSaveBlob(blob, filename);
        }
        else {
            var link = document.createElement("a");
            if (link.download !== undefined) {

                // feature detection, Browsers that support HTML5 download attribute
                var url = URL.createObjectURL(blob);
                link.setAttribute("href", url);
                link.setAttribute("download", filename);

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    }

    convertArrayOfObjectsToCsv(args) {
        var result: string, ctr: number;

        var data = args.data || null;
        if (data == null || !data.length) {
            return null;
        }

        var columnDelimiter = args.columnDelimiter || ",";
        var lineDelimiter = args.lineDelimiter || "\n";

        var keys = Object.keys(data[0]);

        result = "";
        result += keys.join(columnDelimiter);
        result += lineDelimiter;

        data.forEach(item => {
            ctr = 0;
            keys.forEach(key => {
                if (ctr > 0) result += columnDelimiter;

                result += item[key];
                ctr++;
            });
            result += lineDelimiter;
        });

        return result;
    }

    mapEventCode(alcolockEventCode: AlcolockEventCode): string {
        switch (alcolockEventCode) {
            case AlcolockEventCode.PowerUp:
                return this.translationService.t("AlcolockCompliance.PowerUp");
            case AlcolockEventCode.InitialSampleFailed:
                return this.translationService.t("AlcolockCompliance.InitialSampleFailed");
            case AlcolockEventCode.RandomSampleFailed:
                return this.translationService.t("AlcolockCompliance.RandomSampleFailed");
            case AlcolockEventCode.InitialSamplePassed:
                return this.translationService.t("AlcolockCompliance.InitialSamplePassed");
            case AlcolockEventCode.RandomSamplePassed:
                return this.translationService.t("AlcolockCompliance.RandomSamplePassed");
            case AlcolockEventCode.PossiblePushStart:
                return this.translationService.t("AlcolockCompliance.PossiblePushStart");
            case AlcolockEventCode.OverridePeriodStarted:
                return this.translationService.t("AlcolockCompliance.OverridePeriodStarted");
            case AlcolockEventCode.OverridePeriodEnded:
                return this.translationService.t("AlcolockCompliance.OverridePeriodEnded");
            case AlcolockEventCode.EmergencyOverridePeriodStarted:
                return this.translationService.t("AlcolockCompliance.EmergencyOverridePeriodStarted");
            case AlcolockEventCode.ForcedSampleRequest:
                return this.translationService.t("AlcolockCompliance.ForcedSampleRequest");
            case AlcolockEventCode.RandomSampleRequest:
                return this.translationService.t("AlcolockCompliance.RandomSampleRequest");
            case AlcolockEventCode.CallTimeExpired:
                return this.translationService.t("AlcolockCompliance.CallTimeExpired");
            case AlcolockEventCode.CircumventionBreathRelated:
                return this.translationService.t("AlcolockCompliance.CircumventionBreathRelated");
            case AlcolockEventCode.CircumventionSuckBack:
                return this.translationService.t("AlcolockCompliance.CircumventionSuckBack");
            case AlcolockEventCode.CircumventionBlowingTooHard:
                return this.translationService.t("AlcolockCompliance.CircumventionBlowingTooHard");
            case AlcolockEventCode.SystemRearmedStartTimeExpired:
                return this.translationService.t("AlcolockCompliance.SystemRearmedStartTimeExpired");
            case AlcolockEventCode.SystemRearmedRestartTimeExpired:
                return this.translationService.t("AlcolockCompliance.SystemRearmedRestartTimeExpired");
            case AlcolockEventCode.TabConnected:
                return this.translationService.t("AlcolockCompliance.TabConnected");
            case AlcolockEventCode.DriverChangeRequest:
                return this.translationService.t("AlcolockCompliance.DriverChangeRequest");
            case AlcolockEventCode.DriverChangePassed:
                return this.translationService.t("AlcolockCompliance.DriverChangePassed");
            case AlcolockEventCode.DriverChangeFailed:
                return this.translationService.t("AlcolockCompliance.DriverChangeFailed");
            case AlcolockEventCode.DriverChangeCallExpired:
                return this.translationService.t("AlcolockCompliance.DriverChangeCallExpired");
            case AlcolockEventCode.ManualSample:
                return this.translationService.t("AlcolockCompliance.ManualSample");
            case AlcolockEventCode.PassedAfterRandomCallExpired:
                return this.translationService.t("AlcolockCompliance.PassedAfteRandomCallExpired");
            case AlcolockEventCode.PassedAfterDriverChangeCallExpired:
                return this.translationService.t("AlcolockCompliance.PassedAfterDriverChangeCallExpired");
            case AlcolockEventCode.FailedAfterRandomCallExpired:
                return this.translationService.t("AlcolockCompliance.FailedAfterRandomCallExpired");
            case AlcolockEventCode.FailedAfterDriverChangeCallExpired:
                return this.translationService.t("AlcolockCompliance.FailedAfterDriverChangeCallExpired");
            case AlcolockEventCode.CircumventionInsufficientSample:
                return this.translationService.t("AlcolockCompliance.CircumventionInsufficientSample");
            case AlcolockEventCode.DoorOpened:
                return this.translationService.t("AlcolockCompliance.DoorOpened");
            case AlcolockEventCode.IgnitionOn:
                return this.translationService.t("AlcolockCompliance.IgnitionOn");
            case AlcolockEventCode.IgnitionOff:
                return this.translationService.t("AlcolockCompliance.IgnitionOff");
            case AlcolockEventCode.DatabaseDeleted:
                return this.translationService.t("AlcolockCompliance.DatabaseDeleted");
            case AlcolockEventCode.ServiceReminder:
                return this.translationService.t("AlcolockCompliance.ServiceReminder");
            case AlcolockEventCode.ServiceExpired:
                return this.translationService.t("AlcolockCompliance.ServiceExpired");
            default:
                return this.translationService.t("AlcolockCompliance.Unknown");
        }
    }
}
